import i18next from 'i18next';
import type { TFunctionDetailedResult } from 'i18next';
import { initReactI18next } from 'react-i18next';
import I18NextChainedBackend from 'i18next-chained-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import ICU from 'i18next-icu';
import HttpBackend from 'i18next-http-backend';
import type { $SpecialObject } from 'i18next/typescript/helpers';
import { fallbackLng, translatedLocales } from '~/config/i18n/languages';
import { i18nFilesLocations } from '~/config/i18n/files-locations';
import { InternationalizationNameSpace } from '~/constants/internationalization-namespace';
import { resources } from './translations';

export const defaultNS = InternationalizationNameSpace.Legacy;

const mediaBucketNss = {
  [InternationalizationNameSpace.Status]: translatedLocales,
  [InternationalizationNameSpace.ErrorCodesTranslationTdrOm]: [fallbackLng],
  [InternationalizationNameSpace.ErrorCodesTranslationRoboticsOm]: translatedLocales,
  [InternationalizationNameSpace.ErrorCodeTitleTranslationRobotics]: translatedLocales,
  [InternationalizationNameSpace.TechnicalInformationTranslation]: translatedLocales,
  [InternationalizationNameSpace.IncidentCommunication]: translatedLocales,
};

/**
 * Namespaces fetched over HTTP from the media bucket. Kept out of the i18next `ns`
 * list so `init()` never waits on them, then preloaded once init resolves.
 */
export const mediaBucketNamespaces = Object.keys(mediaBucketNss);

/**
 * Namespaces bundled into the app build. These resolve from local imports, so
 * gating the first render on them costs no network time. Derived from
 * `i18nFilesLocations` so a new bundled namespace is gated without a second edit.
 */
export const bundledNamespaces = i18nFilesLocations.map(({ ns }) => ns);

export const alternateFetch = (url: string) => {
  if (
    !Object.keys(mediaBucketNss).some((ns: string) =>
      mediaBucketNss[ns].some((lang: string) => url.includes(`${ns}.${lang}`))
    )
  ) {
    return Promise.resolve();
  }
  return undefined;
};

let initPromise: Promise<unknown> | undefined;

/**
 * Initializes i18next for the given locale. Idempotent — later calls return the
 * promise from the first one. Media bucket namespaces preload on a separate
 * chain, so a failure there never settles the returned promise.
 *
 * @returns a promise that resolves once the bundled namespaces are loaded.
 */
export const initInternationalization = (locale: string): Promise<unknown> => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = i18next
    .use(initReactI18next)
    .use(ICU)
    .use(I18NextChainedBackend)
    .init({
      lng: locale,
      fallbackLng: { nb: ['no', fallbackLng], default: [fallbackLng] },
      load: 'languageOnly',
      ns: bundledNamespaces,
      initAsync: false,
      react: {
        // react-redux 9 routes useSelector through useSyncExternalStore, so store
        // updates render at sync priority. A namespace that suspends during such a
        // render throws React error #426 and blanks the app. Namespaces loaded over
        // HTTP from the media bucket can suspend for seconds on a slow connection.
        useSuspense: false,
        transSupportBasicHtmlNodes: true, // allow <br/> and simple html elements in translations
        transKeepBasicHtmlNodesFor: ['br', 'strong', 'bold', 'i', 'b', 'li', 'ul', 'p', 'ol'], // don't convert to <1></1> if simple react elements
        transWrapTextNodes: '',
      },
      defaultNS,
      backend: {
        crossDomain: true,
        backends: [
          resourcesToBackend((language: string, namespace: InternationalizationNameSpace) =>
            resources[language]?.[namespace]?.()
          ),
          HttpBackend,
          HttpBackend,
        ],
        backendOptions: [
          {
            loadPath: './locales/{{ns}}.{{lng}}.json',
          },
          {
            loadPath: `${process.env.MEDIA_BUCKET_BASE_URL}/translations/{{ns}}.{{lng}}.json`,
            alternateFetch,
          },
          {
            loadPath: '/translations/{{ns}}.{{lng}}.json',
            alternateFetch,
          },
        ],
      },
    });

  void initPromise.then(() => i18next.loadNamespaces(mediaBucketNamespaces)).catch(() => undefined);

  return initPromise;
};

/**
 * @returns the pending or settled i18next init promise, or a resolved promise
 * when `initInternationalization` has not been called.
 */
export const getI18nInitPromise = (): Promise<unknown> => initPromise ?? Promise.resolve();

export type TranslatorFn = (
  key: string,
  values?: any
) => string | $SpecialObject | TFunctionDetailedResult<string | $SpecialObject, any>;
